import { ContentFile, ContentFileThumbnailType } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import {
  ContentFeed,
  ContentFeedSearchResult,
  InputContentFeedSearch,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";

import providers from "@/lib/api";
import { isVideoFile } from "@/lib/fileHelpers";

const getFeedIdsToUpdate = (authorId: string, feeds: ContentFeed[], purchasedFeedId?: string) => {
  if (typeof purchasedFeedId === "string") return [purchasedFeedId];

  return feeds.reduce<string[]>((acc, feed) => {
    if (!feed.purchased && feed.userId === authorId) {
      acc.push(feed.id);
    }
    return acc;
  }, []);
};

const getUpdatedList = (currentList: ContentFeedSearchResult, updatedItems: ContentFeed[]) => {
  // todo: mutates the initial list. Use with caution or fix
  let newList = currentList.result.map(i => {
    const index = updatedItems.findIndex(item => item.id === i.id);
    let item = i;
    if (index > -1) {
      item = updatedItems.splice(index, 1)[0];
    }
    return item;
  });
  if (updatedItems.length > 0) {
    if (currentList.hasNextPage) {
      newList.splice(newList.length - updatedItems.length, updatedItems.length);
    }
    newList = updatedItems.concat(newList);
  }
  return newList;
};

export const getPurchasedContentFeeds = async (
  { authorId, purchasedFeedId }: { authorId: string; purchasedFeedId?: string },
  contentFeedSearchResult: ContentFeedSearchResult,
  requestData?: Partial<InputContentFeedSearch>,
  requestString?: string
): Promise<ContentFeed[]> => {
  const feedIds = getFeedIdsToUpdate(authorId, contentFeedSearchResult.result, purchasedFeedId);
  if (feedIds.length === 0) return contentFeedSearchResult.result;
  const response = await providers.ContentFeedProvider.search(
    {
      data: {
        userId: authorId,
        ids: feedIds,
        size: feedIds.length,
        includeScheduled: false,
        published: true,
        ...requestData,
      },
    },
    requestString
  );
  return getUpdatedList(contentFeedSearchResult, response.result);
};

export const getFeedCoParticipants = (feed: Pick<ContentFeed, "participants">): UserPublicData[] => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return (feed.participants ?? []).filter(p => !!p.user).map(p => p.user!);
};

export const getFeedParticipants = (feed: ContentFeed): UserPublicData[] => {
  return [feed.user, ...getFeedCoParticipants(feed)];
};

// a viewer in the NSFW mode gets the SFW version when the post has no NSFW one, but never the other way round
export const getFeedThumbnail = (files: ContentFile[], showNotSafeForWork: boolean): ContentFile | null => {
  const sfwThumbnail = files.find(f => f.thumbnailType === ContentFileThumbnailType.SFW) ?? null;
  if (!showNotSafeForWork) return sfwThumbnail;
  return files.find(f => f.thumbnailType === ContentFileThumbnailType.NSFW) ?? sfwThumbnail;
};

// the trailer is picked by the same rule as in videoToShow of ContentFeedVideoGallery.vue
export const getFeedTrailer = (files: ContentFile[]): ContentFile | null => {
  const publicVideos = files.filter(f => f.publicAccess && !f.thumbnailType && isVideoFile(f));
  const byDuration = publicVideos.filter(v => v.duration > 0).sort((a, b) => a.duration - b.duration);
  return byDuration[0] ?? publicVideos[0] ?? null;
};
